// The recap's sources (gate D2.3, decision 0063; phase D joined): the story's current objective (seam 5's
// `objective()`) and its event log's last entries ("what happened"), each said through the strings table, then where the
// Wanderer was (the world's region). A page with no story (the labs, `?story=off`) has no objective and only the place.
import type { LogEntry } from "@rpg/story";
import type { SpikeContent } from "../content";

/** What the recap shows. */
export interface RecapSource {
  /** The current objective's label (translated), or null. */
  objective(): string | null;
  /** The last `n` things that happened, newest last (translated). */
  recent(n: number): string[];
}

/** The story as the recap reads it. */
export interface RecapStory {
  /** The objective marker's label (a string id), or null. */
  objective(): { label: string } | null;
  /** The event log, newest first. */
  deeds(limit: number): readonly LogEntry[];
}

/** The recap over the story (null: none in this page) and the world's region. */
export function storyRecap(d: { content: SpikeContent; story: () => RecapStory | null; region: () => string | null }): RecapSource {
  const t = d.content.t;
  const reg = d.content.registry;
  const nameOf = (table: "enemies" | "items" | "npcs", id: string): string => {
    const row = reg.get(table, id) as { name?: { id: string } } | undefined;
    return row?.name ? t(row.name.id) : id;
  };
  const placeOf = (r: string | null | undefined): string => t(`recap.place.${r && d.content.strings.has(`recap.place.${r}`) ? r : "valley"}`);
  const questTitle = (id: string): string => {
    const row = reg.get("quests", id) as { name?: { id: string } } | undefined;
    return row?.name ? t(row.name.id) : id;
  };
  /** A deed in words, or null for one the recap doesn't tell (a scene, a quest offered). */
  const say = (e: LogEntry): string | null => {
    switch (e.kind) {
      case "defeated":
        return t("recap.deed.defeated", { name: nameOf("enemies", e.subject) });
      case "gathered":
        return t("recap.deed.gathered", { name: nameOf("items", e.subject) });
      case "talked":
        return t("recap.deed.talked", { name: nameOf("npcs", e.subject) });
      case "gift":
        return t("recap.deed.gift", { name: nameOf("npcs", e.subject) });
      case "entered":
        return d.content.strings.has(`recap.place.${e.subject}`) ? t("recap.deed.entered", { place: placeOf(e.subject) }) : null;
      case "quest": {
        const state = (e.data as { state?: string } | undefined)?.state;
        if (state === "active") return t("recap.deed.questTaken", { title: questTitle(e.subject) });
        if (state === "done") return t("recap.deed.questDone", { title: questTitle(e.subject) });
        return null;
      }
      default:
        return null;
    }
  };
  return {
    objective: () => {
      const o = d.story()?.objective();
      return o ? t(o.label) : null;
    },
    recent: (n) => {
      const out: string[] = [];
      const story = d.story();
      // newest first from the log; the same line twice in a row is said once
      for (const e of story ? story.deeds(64) : []) {
        const line = say(e);
        if (!line || out[out.length - 1] === line) continue;
        out.push(line);
        if (out.length >= Math.max(0, n - 1)) break;
      }
      return [...out.reverse(), t("recap.place", { place: placeOf(d.region()) })].slice(-n);
    },
  };
}
